/***************
* *******
* *******
* Heuristic Policy
* *******
* *******
*
* Given a State, chooses an Action
*
* In this context, "State" is defined as an object which contains all features necessary
* to choose an intervention. "Action" is a selected intervention.
*
* - interveneSelection chooses what word to intervene on, as necessary
* - interventionSelection chooses which intervention to use, given chosen word and state
*
* While this implementation strives to come as close as possible to a being a pure function,
* the policy also mantains an internal representation of state over time.
*
* In particular, "ready" and "frustration" scores are recalculated after an intervention
* has taken place.

Exports three items:

#1 //// selectAction
 - Given a State, selects an Action

#2 //// initializeWithCustomConfigs
 - A function, for testing with customized configs

#3 //// defaultPassConfigs
 - A constant, containing the default configs
****************/

// Score passes
// Note: a "run" is defined here as the behavior of selectAction(), given a particular State
import getReadyScoresForRun from './score_passes/score_pass_1';
import getFrustrationScoreForRun from './score_passes/score_pass_2';

// Intervene and intervention selection
import selectWord from './intervene_selector';
import { selectIntervention } from './intervention_selector';

// Pause calculation
import calculatePauseLength from './pause_calculation';

// Configurations for intervention selection passes and score passes
import getConfigForLocale from './config/getConfigForLocale';

//////
// This function selects an action given a state. It is the heart of the policy.
export function selectAction(state) {
  const passConfigs = getConfigForLocale(state.locale);

  let action;
  // If this state indicates that we're setting configurations prior to an expected utterance (e.g. pause time)
  if(state.wordPrep) {
    action = calculatePauseLength(state);
  } else { // Else, select an intervention.
    // First, calculate Frustration, Occurence, and Ready scores, given state
    state.scores = {};
    // Score pass #1 generates any intervention-specific derived features, based on intervention history
    getReadyScoresForRun(state, passConfigs);
    // Score pass #2 calculates frustration based on intervention history
    getFrustrationScoreForRun(state, passConfigs); // Score pass #2

    // Then, choose a word to intervene on, and an intervention
    selectWord(state); // Updates state to include a selected word, if necessary. Also updates disabled interventions based on selection
    action = selectIntervention(state);
  }

  action.metadata = state;
  return action; // Returns a selected action
}

//////
// Allow the user to specify a set of custom configs.
/*
This function's use case is to enable trying out different configs in the test app.
*/
export function initializeWithCustomConfigs(CUSTOM_CONFIGS) {
  // passConfigs = CUSTOM_CONFIGS;
  initializeInterventionPassesWithCustomConfigs(CUSTOM_CONFIGS);
}

//////
// Export PASS_CONFIGS, so that testing app can load defaults.
/*
Note: The exported version is "partially stringified", such that arrow functions
are represented with strings, so that they can be modified in the test-app JSON editor.
*/
// export const defaultPassConfigs = PARTIALLY_STRINGIFIED_DEFAULT_CONFIGS;
